import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, MapPin, Search, Star, SlidersHorizontal } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { useFarms, useCepLookup } from '../hooks/useFarms';
import { staggerContainer, staggerItem } from '@/lib/motion';
import type { FarmNearby } from '../types';
import { MATERIALS, SPECIALIZATIONS } from '../lib/machinesCatalog';

const BR_STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];

function FarmCard({ farm }: { farm: FarmNearby }) {
  const navigate = useNavigate();
  return (
    <motion.div
      variants={staggerItem}
      whileHover={{ y: -3, transition: { duration: 0.18 } }}
      className="card p-4 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate(`/farms/${farm.slug}`)}
    >
      <div className="flex items-start gap-3">
        {farm.logo_url ? (
          <img src={farm.logo_url} alt={farm.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
            <span className="text-2xl">🖨️</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-ink">{farm.name}</p>
          <p className="text-xs text-ink-muted flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" />
            {farm.city} · {farm.state_uf} · {farm.distance_km.toFixed(0)} km
          </p>
          <div className="flex items-center gap-1 mt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${i < Math.round(farm.avg_rating) ? 'text-warning fill-warning' : 'text-border'}`}
              />
            ))}
            <span className="text-xs text-ink-muted ml-1">
              {farm.avg_rating.toFixed(1)} ({farm.reviews_count})
            </span>
          </div>
        </div>
        <span className="btn-secondary !py-1 !text-xs shrink-0">Ver →</span>
      </div>
    </motion.div>
  );
}

export function Marketplace() {
  const { farms, loading, error, search } = useFarms();
  const { lookup, loading: cepLoading, error: cepError } = useCepLookup();

  const [cep, setCep]             = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [material, setMaterial]   = useState('');
  const [stateUf, setStateUf]     = useState('');
  const [spec, setSpec]           = useState('');
  const [radius, setRadius]       = useState(200);
  const [searched, setSearched]   = useState(false);
  const [coords, setCoords]       = useState<{ lat: number; lng: number } | null>(null);

  async function handleSearch() {
    let lat: number | undefined = coords?.lat;
    let lng: number | undefined = coords?.lng;
    if (!lat || !lng) {
      if (!cep.trim()) return;
      const geo = await lookup(cep.trim());
      if (!geo || !geo.lat || !geo.lng) return;
      lat = geo.lat;
      lng = geo.lng;
      setCoords({ lat, lng });
    }
    await search({ lat, lng, radius_km: radius, material: material || undefined, state_uf: stateUf || undefined, specialization: spec || undefined });
    setSearched(true);
  }

  return (
    <PageShell title="MYND Farms" subtitle="Encontre farms de impressão 3D próximas a você." maxWidth="xl">
      {/* Busca */}
      <div className="card p-5 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-xs text-ink-muted mb-1">Seu CEP</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
              <input
                type="text"
                className="input w-full pl-9"
                placeholder="00000-000"
                value={cep}
                onChange={(e) => setCep(e.target.value.replace(/\D/g, '').slice(0, 8))}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            {cepError && <p className="text-xs text-negative mt-1">{cepError}</p>}
          </div>
          <div className="flex items-end gap-2">
            <button onClick={() => setShowFilters((v) => !v)} className="btn-secondary gap-2">
              <SlidersHorizontal className="w-4 h-4" />Filtros
            </button>
            <button onClick={handleSearch} disabled={loading || cepLoading} className="btn-primary gap-2">
              {(loading || cepLoading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-border">
            <div>
              <label className="block text-xs text-ink-muted mb-1">Raio (km)</label>
              <input type="number" className="input w-full" value={radius} min={10} max={2000}
                onChange={(e) => setRadius(Number(e.target.value))} />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Material</label>
              <select className="input w-full" value={material} onChange={(e) => setMaterial(e.target.value)}>
                <option value="">Todos</option>
                {MATERIALS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Estado</label>
              <select className="input w-full" value={stateUf} onChange={(e) => setStateUf(e.target.value)}>
                <option value="">Todos</option>
                {BR_STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Especialização</label>
              <select className="input w-full" value={spec} onChange={(e) => setSpec(e.target.value)}>
                <option value="">Todas</option>
                {SPECIALIZATIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-negative mb-4">{error}</p>}

      {!searched ? (
        <EmptyState icon={MapPin} title="Digite seu CEP para encontrar farms próximas" description="Farms de impressão 3D disponíveis na sua região." />
      ) : farms.length === 0 ? (
        <EmptyState icon={Search} title="Nenhuma farm encontrada" description="Tente aumentar o raio de busca ou remover filtros." />
      ) : (
        <motion.div
          className="space-y-3"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <p className="text-sm text-ink-muted">{farms.length} farm(s) encontrada(s)</p>
          {farms.map((f) => <FarmCard key={f.farm_id} farm={f} />)}
        </motion.div>
      )}
    </PageShell>
  );
}
